import React, { useEffect } from "react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser, faFloppyDisk, faXmark } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import Card from "../Card";
import axios from "axios";
import useAppData from "@/store/useAppData";
import { stockData } from "./types";
import { ItemRowValue } from "../ItemRow";
import { StockItemTypeOdmraz } from "@/Utils/constants";

export interface appStockCardOdmrazProps {
    stockId?: string;
    onSave?: (stockId: string) => void;
}

const StockCardOdmraz: React.FC<appStockCardOdmrazProps> = ({ stockId, onSave }) => {

    const selectedModule = useAppData(state => state.selectedModule);

    const [name, setName] = React.useState<string | null>(null);
    const [sapInv, setSapInv] = React.useState<string | null>(null);
    const [sapIm, setSapIm] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [mainStock, setMainStock] = React.useState<boolean | null>(null);
    const [twoOperators, setTwoOperators] = React.useState<boolean | null>(null);

    useEffect(() => {
        setError(null);

        const loadData = async () => {

            if (stockId) {
                loadStock(stockId);
            }
        };

        loadData();
    }, []);

    const setStockData = (stock: stockData) => {
        setName(stock.name);
        setSapInv(stock.SAPInvMajetek);
        setSapIm(stock.SAPOznaceniIM);
        setMainStock(stock.main);
        setTwoOperators(stock?.twoOperators);
    }

    const loadStock = async (stockId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/get",
                    data: {
                        id: stockId
                    }
                })
            );

            if (res.data.stocks.length > 0) {
                const stock: stockData = res.data.stocks[0];
                setStockData(stock);
            }
        } catch (err) {
            throw err;
        }
    }

    const insertStock = async (name: string, sAPInvMajetek: string, sAPOznaceniIM: string, main: boolean, twoOperators: boolean) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/insert",
                    data: {
                        name: name,
                        sAPInvMajetek: sAPInvMajetek,
                        sAPOznaceniIM: sAPOznaceniIM,
                        module: selectedModule?.id,
                        usecleaners: false,
                        main: main,
                        twoOperators: twoOperators
                    }
                })
            );

            return res.data.id;
        } catch (err) {
            throw err;
        }
    }

    const insertStockItem = async (item: ItemRowValue) => {
        try {
            const res = await axios({
                method: "post",
                url: "/api/stockitem/insert",
                data: {
                    name: item.name,
                    quantity: item.quantity,
                    stockId: item.stockId,
                    autofill: item.autofill
                },
            });

            return res.data.id;
        } catch (err) {
            console.error("Failed to insert stock item", err);
            throw err;
        }
    };

    const updateStock = async (idStock: string, name: string, sAPInvMajetek: string, sAPOznaceniIM: string, main: boolean, twoOperators: boolean) => {
        try {
            await (
                axios({
                    method: "post",
                    url: "api/stock/update",
                    data: {
                        id: idStock,
                        name: name,
                        sAPInvMajetek: sAPInvMajetek,
                        sAPOznaceniIM: sAPOznaceniIM,
                        main: main,
                        twoOperators: twoOperators
                    }
                })
            );
        } catch (err) {
            throw err;
        }
    }

    const validateNewStock = () => {
        const fn = name?.trim();

        if (stockId) {
            if (!fn) {
                return "Vyplňte Název.";
            }
        }

        return null;
    };

    const onCreate = async () => {
        setError(null);

        const validationError = validateNewStock();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (stockId) {
            await updateStock(stockId, name, sapInv, sapIm, mainStock, twoOperators);
            onSave(stockId);
        } else {

            const newStockId = await insertStock(name, sapInv, sapIm, mainStock, twoOperators);

            const stockItem1 = {
                name: StockItemTypeOdmraz.Type1,
                quantity: 0,
                stockId: newStockId
            };

            insertStockItem(stockItem1);

            const stockItem2 = {
                name: StockItemTypeOdmraz.Type2,
                quantity: 0,
                stockId: newStockId
            };

            insertStockItem(stockItem2);

            const stockItem3 = {
                name: StockItemTypeOdmraz.Water,
                quantity: 0,
                stockId: newStockId,
                autofill: true
            };

            insertStockItem(stockItem3);

            onSave(newStockId);
        }
    };

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => onSave(null),
        },
        {
            key: "save",
            label: "Uložit",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: onCreate,
        },
    ];

    return (
        <Card title="Sklad" suppressInit={true} displaySelectedStock={false}>
            <div className="nec-stack">
                <>
                    <div className="nec-block">
                        <div className="nec-header">Název</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={name}
                                required
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Název..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">SAPInvMajetek</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={sapInv}
                                onChange={(e) => setSapInv(e.target.value)}
                                placeholder="Hodnota..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">SAPOznaceniIM</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={sapIm}
                                onChange={(e) => setSapIm(e.target.value)}
                                placeholder="Hodnota..."
                            />
                        </div>
                    </div>

                    <div className="nec-block">
                        <div className="nec-header">Hlavní sklad</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="checkbox"
                                checked={mainStock ?? undefined}
                                onChange={(e) => setMainStock(e.target.checked)}
                                placeholder="Heslo..."
                            />
                        </div>
                    </div>
                    <div className="nec-block">
                        <div className="nec-header">Více operátorů</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                type="checkbox"
                                checked={twoOperators ?? undefined}
                                onChange={(e) => setTwoOperators(e.target.checked)}
                                placeholder="Více operátorů"
                            />
                        </div>
                    </div>
                </>

                {error ? <div className="form-error">{error}</div> : null}
            </div>

            {actions?.length ? (
                <div className="item-list-actions">
                    <FloatingActions actions={actions} />
                </div>
            ) : null}
        </Card>
    );

};

export default StockCardOdmraz;